import { BREAK_POINT, MEDIA_QUERY } from '@bucketplace/design-system';
import { getVar, useTheme } from '@bucketplace/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { REBRANDING_THEME } from '@/brand/common/style';
import type { AnimationComponentRef } from '@/brand/hooks';

import { CONTENTS_CARD_GAP, CONTENTS_ITEMS } from '../constants';
import { Container } from '../RebrandSectionContainer';
import { getBreakPoint } from '../utils';
import { ContentsCardLayout } from './ContentsCardLayout';

const PADDING_LEFT = {
  mobile: 20,
  tablet: 20,
  laptop: 60,
  desktop: 60,
};

// ScrollTrigger 플러그인 등록
gsap.registerPlugin(ScrollTrigger);

export const ContentsSection = forwardRef<AnimationComponentRef>((_, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const { cssVar } = useTheme<typeof REBRANDING_THEME>();
  // const isMobile = isMobileView(breakpoint);

  // const calcCallback = useCallback(() => {
  //   if (!containerRef.current) return 0;

  //   // 실제 컨테이너의 사용 가능한 너비 (CSS 미디어쿼리 모두 반영됨)
  //   const containerWidth = containerRef.current.clientWidth;

  //   // 카드 총 너비
  //   const totalWidth = CONTENTS_ITEMS.reduce((acc, item) => acc + item.width, 0) +
  //     (CONTENTS_ITEMS.length - 1) * 20;

  //   const paddingRight = isMobile ? PADDING_LEFT.mobile : PADDING_LEFT.laptop;
  //   const effectiveWidth = containerWidth - paddingRight;

  //   return totalWidth > effectiveWidth ? -(totalWidth - effectiveWidth) : 0;
  // }, [isMobile]);

  const calcCallback = useCallback(() => {
    if (!containerRef.current) {
      return 0;
    }

    if (!cardContainerRef.current) {
      return 0;
    }

    const breakpoint = getBreakPoint(containerRef.current.clientWidth);

    // CardContainer의 실제 사용 가능한 너비 (패딩이 이미 적용된 상태)
    const effectiveWidth = cardContainerRef.current.clientWidth;

    // 카드 총 너비
    const totalWidth =
      CONTENTS_ITEMS.reduce((acc, layout) => {
        if (layout.flexDirection === 'row') {
          return (
            acc +
            layout.items.reduce((acc, item) => acc + item.width[breakpoint], 0)
          );
        }

        return acc + layout.items[0].width[breakpoint];
      }, 0) +
      (CONTENTS_ITEMS.length - 1) * CONTENTS_CARD_GAP['mobile'] +
      PADDING_LEFT['mobile'];
    // const totalWidth = cardContainerRef.current.clientWidth;

    return totalWidth > effectiveWidth ? -(totalWidth - effectiveWidth) : 0;
  }, []);

  const config = useMemo(
    () => ({
      steps: [
        {
          target: containerRef,
          from: { opacity: 0, y: 50 },
          to: { opacity: 1, y: 0, duration: 0.05 },
          position: '>',
        },
        {
          target: cardContainerRef,
          from: { x: 0 },
          to: {
            x: calcCallback,
            duration: 0.75,
            force3D: true,
          },
          position: '+=0.2',
        },
      ],
      options: {
        trigger: containerRef,
        start: 'top top',
        end: '+=600%',
        scrub: 1,
        invalidateOnRefresh: true,
        refreshPriority: 1,
        pin: false,
        anticipatePin: 0,
        normalizeScroll: true,
      },
    }),
    [calcCallback],
  );

  useImperativeHandle(
    ref,
    () => ({
      createTimeline() {
        const tl = gsap.timeline({
          defaults: {
            ease: 'none',
          },
        });
        for (const s of config.steps) {
          const el = s.target?.current;
          if (!el) {
            continue;
          }
          if (s.from) {
            tl.fromTo(el, s.from, s.to, s.position ?? '>');
          } else {
            tl.to(el, s.to, s.position ?? '>');
          }
        }
        return tl;
      },
    }),
    [config.steps],
  );

  // const handleResize = useCallback((entry: ResizeObserverEntry) => {
  //   // GSAP 애니메이션 새로고침
  //   if (refreshAnimation) {
  //     requestAnimationFrame(() => {
  //       refreshAnimation();
  //     });
  //   }
  // }, [refreshAnimation]);

  // 🔥 훅 사용 - 매우 간단!
  // useResizeObserver(containerRef, () => {
  //   requestAnimationFrame(() => {
  //     ScrollTrigger.refresh();
  //   });
  // });

  // useEffect(() => {
  //   requestAnimationFrame(() => {
  //     ScrollTrigger.refresh();
  //   });
  // }, [breakpoint]);
  // console.log('ContentsSection 리렌더링됨', cardContainerRef);

  return (
    <Container
      ref={containerRef}
      gap={{
        mobile: 36,
        tablet: 36,
        laptop: 60,
        desktop: 60,
      }}
      pl={PADDING_LEFT}
    >
      <Title color={getVar(cssVar('contents-title'))}>
        {'모두의 이야기로\n함께 채워갑니다'}
      </Title>
      <CardContainer ref={cardContainerRef}>
        {CONTENTS_ITEMS.map((layout, index) => (
          <ContentsCardLayout key={layout.id} layoutIndex={index} {...layout} />
        ))}
      </CardContainer>
    </Container>
  );
});

const Title = styled.p`
  width: 100%;
  font-size: 28px;
  line-height: 36px;
  font-weight: 700;
  letter-spacing: -0.3px;
  white-space: pre-line;
  text-align: center;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    text-align: start;
    max-width: ${BREAK_POINT.laptop}px;
    font-size: 44px;
    line-height: 56px;
    letter-spacing: -0.44px;
  `,
  )}
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${CONTENTS_CARD_GAP.mobile}px;
  overflow: visible;
  width: 100%;
  max-width: ${BREAK_POINT.laptop}px;
  padding-right: 20px;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    max-width: ${BREAK_POINT.laptop}px;
    padding-right: 60px;
    gap: ${CONTENTS_CARD_GAP.laptop}px;
  `,
  )}
  
  /* 각 카드가 축소되지 않도록 설정 */
  > * {
    flex-shrink: 0;
  }
`;
